import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography, useTheme, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useGetAgencyConfiguration, useUpdateAgencyConfiguration } from 'apis/agency/configuration/hook';
import InfoIcon from 'assets/icons/InfoIcon';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import Spinner from 'components/Common/Spinner';
import { useForm } from 'react-hook-form';
import { smtpSchema } from 'utils/schema/validator/AgencyConfigureValidation';

export default function Smtp() {
  const theme = useTheme();
  const { data: configuration, isLoading } = useGetAgencyConfiguration();
  const { mutateAsync: updateConfiguration } = useUpdateAgencyConfiguration();

  const { handleSubmit, control, reset } = useForm({
    resolver: zodResolver(smtpSchema),
    values: { configuration: configuration }
  });

  const onSubmit = (data: any) => {
    updateConfiguration(data);
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Typography variant='h6'>SMTP</Typography>
      <Typography color='secondary' mt={1}>
        It simplifies email communications, enabling automated notifications, invoicing, and client updates through
        secure email delivery.
      </Typography>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
        <Grid
          container
          spacing={2}
          bgcolor={theme.palette.background.paper}
          padding={3}
          borderRadius='4px'
          border='1px solid #ECECEC'
        >
          <Box
            sx={{
              backgroundColor: '#F0F6FF',
              border: '1px solid #D0E3FF',
              borderRadius: 2,
              padding: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Box
              component='span'
              sx={{
                width: 24,
                height: 24,
                mt: '2px',
                color: '#1E66F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <InfoIcon />
            </Box>
            <Box>
              <Typography fontWeight={600} mb={0.5} color='#1E66F5'>
                Email Service Information
              </Typography>
              <Typography color='#1E66F5'>
                This SMTP configuration will be used for sending all platform emails including notifications,
                verifications, and other transactional communications.
              </Typography>
            </Box>
          </Box>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP Username
            </Typography>
            <Input name='configuration.smtp_username' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP Password
            </Typography>
            <PasswordInput name='configuration.smtp_password' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP Address
            </Typography>
            <Input name='configuration.smtp_address' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP Domain
            </Typography>
            <Input name='configuration.smtp_domain' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP Port
            </Typography>
            <Input name='configuration.smtp_port' type='number' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP From Email
            </Typography>
            <Input name='configuration.smtp_from' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SMTP From Name
            </Typography>
            <Input name='configuration.smtp_from_name' control={control} size='small' fullWidth />
          </Grid>
        </Grid>
        <Box mt={3} display='flex' gap={1} justifyContent='end'>
          <Button color='secondary' variant='outlined' sx={{ border: '1px solid #ECECEC' }} onClick={() => reset()}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' color='primary'>
            Configure
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
